import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  useCompanyConnectorControllerGetCompanyConnectorsQuery,
  useCompanyConnectorControllerGetAirbyteSetupErrorQuery,
} from "~/ui-rtk/api/companyConnectorApi"
import { ConnectionStatus } from "~/ui-rtk/api/types"
import { useAirbytePollingInterval } from "~/ui-rtk/hooks/airbyte-polling"
import {
  AIRBYTE_POLLING_PREFIX,
  expireStaleAirbytePolling,
  getActiveAirbytePollingKeys,
  reconcileAirbytePolling,
} from "~/ui-rtk/utils/airbyte-polling"

/**
 * Component to fetch and display error messages for failed connectors
 */
const ErrorMessageFetcher: React.FC<{
  connectorId: string
  serviceName: string
}> = ({ connectorId, serviceName }) => {
  const { data: errorData } =
    useCompanyConnectorControllerGetAirbyteSetupErrorQuery(
      { id: connectorId },
      { skip: false },
    )

  useEffect(() => {
    if (errorData?.message) {
      toast.error(errorData.message, {
        toastId: `airbyte-background-error-${connectorId}`,
      })
    } else {
      toast.error(
        `Failed to complete ${serviceName} setup. Please try again.`,
        {
          toastId: `airbyte-background-error-${connectorId}`,
        },
      )
    }
  }, [errorData, connectorId, serviceName])

  return null
}

/**
 * Drives polling for in-progress Airbyte setups via RTK Query's `pollingInterval`.
 * The interval is reactive: zero when no setup is in progress, which means
 * `useCompanyConnectorControllerGetCompanyConnectorsQuery` subscribers stop
 * triggering network requests on idle. Polling state lives in sessionStorage
 * so it survives navigation between the connectors page and the Airbyte
 * config form.
 */
export const AirbytePollingManager: React.FC = () => {
  const pollingInterval = useAirbytePollingInterval()

  const { data: connectors } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery(undefined, {
      pollingInterval,
    })

  const [failedConnectorIds, setFailedConnectorIds] = useState<string[]>([])

  // Reconcile sessionStorage against the latest connector list: drop polling
  // entries whose connector has reached a terminal status or no longer exists,
  // and surface failed connectors via the error fetcher below.
  useEffect(() => {
    if (!connectors) return

    const knownIds = new Set(connectors.map(cc => cc.id))
    const terminalIds = new Set(
      connectors
        .filter(
          cc =>
            cc.status === ConnectionStatus.CONNECTED ||
            cc.status === ConnectionStatus.PENDING_RECONNECT,
        )
        .map(cc => cc.id),
    )

    const pollingKeys = getActiveAirbytePollingKeys()
    const failed: string[] = []
    for (const key of pollingKeys) {
      const connectorId = key.slice(AIRBYTE_POLLING_PREFIX.length)
      const connector = connectors.find(cc => cc.id === connectorId)
      if (connector?.status === ConnectionStatus.PENDING_RECONNECT) {
        failed.push(connectorId)
      }
    }

    reconcileAirbytePolling(terminalIds, knownIds)

    if (failed.length > 0) {
      setFailedConnectorIds(prev => {
        const next = Array.from(new Set([...prev, ...failed]))
        return next.length === prev.length ? prev : next
      })
    }
  }, [connectors])

  // Clear failed ids once the underlying connector has left PENDING_RECONNECT
  // (e.g. user clicks reconnect and setup restarts).
  useEffect(() => {
    if (!connectors || failedConnectorIds.length === 0) return
    setFailedConnectorIds(prev =>
      prev.filter(id => {
        const connector = connectors.find(cc => cc.id === id)
        return connector?.status === ConnectionStatus.PENDING_RECONNECT
      }),
    )
  }, [connectors, failedConnectorIds.length])

  // Expire polling entries that have exceeded the max duration so a crashed
  // or forgotten setup can't keep the poller warm forever.
  useEffect(() => {
    if (pollingInterval === 0) return
    expireStaleAirbytePolling()
    const id = setInterval(() => {
      expireStaleAirbytePolling()
    }, 15_000)
    return () => clearInterval(id)
  }, [pollingInterval])

  const failedConnectors = useMemo(
    () =>
      failedConnectorIds
        .map(id => connectors?.find(cc => cc.id === id))
        .filter((cc): cc is NonNullable<typeof cc> => Boolean(cc)),
    [failedConnectorIds, connectors],
  )

  return (
    <>
      {failedConnectors.map(connector => (
        <ErrorMessageFetcher
          key={connector.id}
          connectorId={connector.id}
          serviceName={connector.serviceName}
        />
      ))}
    </>
  )
}
